import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import TypewriterText from '../ui/TypewriterText'

export default function HeroSection() {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  })

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  return (
    <motion.section
      ref={ref}
      id="hero"
      className="relative min-h-[100dvh] flex flex-col items-center justify-center overflow-hidden px-4 sm:px-6"
      style={{ opacity }}
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-primary via-dark-secondary to-dark-primary">
        {/* Floating Hearts - fewer on mobile */}
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-lg sm:text-2xl opacity-20"
            initial={{
              x: `${Math.random() * 100}%`,
              y: '110%',
            }}
            animate={{
              y: '-10%',
              x: `${Math.random() * 100}%`,
            }}
            transition={{
              duration: Math.random() * 15 + 15,
              repeat: Infinity,
              ease: 'linear',
              delay: Math.random() * 10,
            }}
          >
            {['💕', '💖', '💗', '💝', '❤️'][i % 5]}
          </motion.div>
        ))}

        {/* Gradient Orbs - smaller on mobile */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 rounded-full bg-primary-pink/10 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-48 h-48 sm:w-72 sm:h-72 lg:w-96 lg:h-96 rounded-full bg-accent-pink/10 blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 text-center px-4 sm:px-6 w-full max-w-4xl"
        style={{ y }}
      >
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="tag-pill mb-4 sm:mb-8"
        >
          <span>💕</span>
          <span>2025</span>
        </motion.div>

        {/* Main Title */}
        <motion.h1
          className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-serif font-bold mb-4 sm:mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1 }}
        >
          <span className="block text-white mb-1 sm:mb-2">Notre</span>
          <span className="gradient-text text-shadow-glow leading-tight">
            Relationship Wrapped
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-base sm:text-lg md:text-xl lg:text-2xl text-light-pink-100/80 max-w-xs sm:max-w-md md:max-w-xl lg:max-w-2xl mx-auto mb-8 sm:mb-12 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <TypewriterText
            text="Une année d'amour, de rires, et de moments inoubliables"
            speed={40}
            delay={2000}
          />
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.5, duration: 0.8 }}
        >
          <motion.button
            onClick={() => {
              document.getElementById('debuts')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="group relative px-6 py-3 sm:px-8 sm:py-4 bg-primary-pink text-dark-primary text-sm sm:text-base font-semibold rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-pink/30 active:scale-95"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 flex items-center gap-2">
              Découvre Notre Histoire
              <motion.span
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                ↓
              </motion.span>
            </span>
          </motion.button>
        </motion.div>

        {/* Scroll Indicator - hidden on very small screens */}
        <motion.div
          className="hidden sm:block absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4, duration: 1 }}
        >
          <motion.div
            className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-light-pink-100/30 rounded-full flex justify-center"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              className="w-1 h-2 sm:w-1.5 sm:h-3 bg-primary-pink rounded-full mt-1.5 sm:mt-2"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.section>
  )
}
